import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatSearchNumber, getResultCountDisplay, hasHonestTotal, isWindowedSearch } from '../../utils/searchDisplay';

export default function Pagination({ currentPage, pageSize, totalEstimate, currentCount = 0, searchInfo, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(totalEstimate / pageSize));
  const from = currentPage * pageSize + 1;
  const to = Math.min((currentPage + 1) * pageSize, totalEstimate);
  const countDisplay = getResultCountDisplay(totalEstimate, searchInfo, currentCount);

  if (isWindowedSearch(searchInfo)) {
    const hiddenCount = hasHonestTotal(searchInfo)
      ? Math.max(0, Number(totalEstimate || 0) - Number(currentCount || 0))
      : 0;
    return (
      <div className="flex items-center justify-between py-3 text-sm text-text-muted">
        <span title={countDisplay.title}>
          {countDisplay.compact}
        </span>
        {hiddenCount > 0 && (
          <span className="text-xs text-text-disabled">
            Уточните запрос или фильтры, чтобы сузить ещё {formatSearchNumber(hiddenCount)}
          </span>
        )}
      </div>
    );
  }

  if (totalEstimate <= pageSize) return null;

  // Generate page numbers to show
  const pages = [];
  const maxVisible = 7;
  let start = Math.max(0, currentPage - 3);
  let end = Math.min(totalPages - 1, start + maxVisible - 1);
  if (end - start < maxVisible - 1) {
    start = Math.max(0, end - maxVisible + 1);
  }
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div className="flex items-center justify-between py-3">
      <span className="text-sm text-text-muted" title={countDisplay.title}>
        {from}&ndash;{to} {countDisplay.paginationPrefix} {countDisplay.value}
      </span>

      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 0}
          className="p-1.5 rounded-lg text-text-secondary hover:bg-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {start > 0 && (
          <>
            <button
              onClick={() => onPageChange(0)}
              className="w-8 h-8 rounded-lg text-xs text-text-secondary hover:bg-hover"
            >
              1
            </button>
            {start > 1 && <span className="text-text-disabled text-xs">...</span>}
          </>
        )}

        {pages.map(p => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors
              ${p === currentPage
                ? 'bg-blue-600 dark:bg-blue-500 text-white'
                : 'hover:bg-hover text-text-secondary'
              }`}
          >
            {p + 1}
          </button>
        ))}

        {end < totalPages - 1 && (
          <>
            {end < totalPages - 2 && <span className="text-text-disabled text-xs">...</span>}
            <button
              onClick={() => onPageChange(totalPages - 1)}
              className="w-8 h-8 rounded-lg text-xs text-text-secondary hover:bg-hover"
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages - 1}
          className="p-1.5 rounded-lg text-text-secondary hover:bg-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
